import type { ResponseType } from '@/types/common';
import httpUtil from '@/utils/http';

const request = httpUtil.mfd.request;
const prefix = 'api/v1/ai-work-item';

export interface WorkItemSpec {
  summary?: string;
  acceptanceCriteria?: string[];
  affectedRepos?: string[];
  affectedFiles?: string[];
  testPlan?: string;
  constraints?: string;
  reproduction?: string;
}

export interface SaveWorkItemBody {
  rawText?: string;
  type: 'bug' | 'feature';
  title: string;
  spec?: WorkItemSpec;
  conversationId?: string; // 来源 CTC 机器人对话会话ID，供审核页反查原始对话
}

/**
 * 机器人提交工作项入库（需求或 bug），落 spec_ready 进管理员审核队列。
 * 唯一的写接口，任意登录用户可调（后端按 JWT 判定，权限不在前端放开）。
 */
export const saveWorkItem = (body: SaveWorkItemBody): ResponseType => {
  return request({
    url: prefix,
    method: 'POST',
    data: body,
  });
};

export interface WorkItem {
  id: number;
  conversationId?: string; // 来源 CTC 机器人对话会话ID（老工单为空）
  rawText: string;
  type: 'bug' | 'feature';
  status: string;
  title: string;
  spec: WorkItemSpec;
  branchName: string;
  mrUrls: string[] | null;
  blocker: string;
  submitter: string;
  reviewer: string;
  rejectReason: string;
  lastEditedBy: string;
  lastEditedAt: number;
  createdAt: number;
  updatedAt: number;
}

// 以下为管理员后台接口，后端限 admin 角色。

export const listWorkItems = (params: {
  type?: string;
  status?: string;
  page?: number;
  size?: number;
}): ResponseType => {
  return request({ url: prefix, method: 'GET', params });
};

export const getWorkItem = (id: number): ResponseType => {
  return request({ url: `${prefix}/${id}`, method: 'GET' });
};

export const editWorkItem = (body: {
  id: number;
  title?: string;
  type?: string;
  spec?: WorkItemSpec;
}): ResponseType => {
  return request({ url: prefix, method: 'PUT', data: body });
};

export const reviewWorkItem = (body: { id: number; approve: boolean; rejectReason?: string }): ResponseType => {
  return request({ url: `${prefix}/review`, method: 'POST', data: body });
};

/** 研发标记已合并：pr_ready → merged（代码进主线、待上线，非终态，不通知提交人）。 */
export const mergeWorkItem = (body: { id: number }): ResponseType => {
  return request({ url: `${prefix}/merge`, method: 'POST', data: body });
};

/** 研发标记已上线：merged → delivered（终态），后端通知提交人本人「已完成」。 */
export const deliverWorkItem = (body: { id: number }): ResponseType => {
  return request({ url: `${prefix}/deliver`, method: 'POST', data: body });
};

// —— 「我提交的需求进展」：任意登录用户查看/导出本人提交，后端按 JWT 强制按提交人过滤 ——

const minePrefix = 'api/v1/my-work-item';

export interface MyWorkItemQuery {
  status?: string;
  startTime?: number; // 提交时间下界（Unix 秒）
  endTime?: number; // 提交时间上界（Unix 秒）
  page?: number;
  size?: number;
}

/** 我提交的需求进展列表（仅本人，后端按 JWT 过滤，不接受提交人传参）。 */
export const listMyWorkItems = (params: MyWorkItemQuery): ResponseType => {
  return request({ url: minePrefix, method: 'GET', params });
};

/**
 * 提交人编辑自己的需求（仅本人 + 仅「待审核」，后端强校验）。
 * spec 传完整对象（在原 spec 上改 summary/验收标准），避免覆盖丢字段。
 */
export const editMyWorkItem = (body: {
  id: number;
  title?: string;
  type?: string;
  spec?: WorkItemSpec;
}): ResponseType => {
  return request({ url: minePrefix, method: 'PUT', data: body });
};

/** 提交人关闭（撤回）自己的需求（仅本人 + 仅可关状态，后端强校验）。关闭后为终态。 */
export const closeMyWorkItem = (body: { id: number }): ResponseType => {
  return request({ url: `${minePrefix}/close`, method: 'POST', data: body });
};
